import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MessageSquarePlus,
  Send,
  Shield,
  ShieldAlert,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import {
  type CoverModerationBook,
  type CoverModerationResponse,
  getCoverModerationData,
  submitModerationFeedback,
} from '../services/api';

type BadgeConfig = { bg: string; text: string; border: string; label: string };

function getStatusBadge(book: CoverModerationBook): BadgeConfig {
  if (book.cover_explicit === null || book.cover_explicit === undefined) {
    return {
      bg: 'bg-gray-100',
      text: 'text-gray-600',
      border: 'border-gray-200',
      label: 'Not Scanned',
    };
  }
  if (book.cover_explicit === false) {
    return {
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      label: 'Safe',
    };
  }
  return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Flagged' };
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// Build the Indigo cover image URL from the book's identifier. Uses the
// size-controlled dynamic endpoint (not full-size pdp.indigoimages.ca) because
// the moderation grid loads many thumbnails at once.
function getCoverImageUrl(book: CoverModerationBook): string | null {
  const identifier = book.sku || book.book_id;
  if (!identifier) return null;
  const cleanId = identifier.replace(/-/g, '');
  return `https://dynamic.indigoimages.ca/v1/books/books/${cleanId}/1.jpg?width=200&maxHeight=300&quality=85`;
}

function CoverThumbnail({ book }: Readonly<{ book: CoverModerationBook }>) {
  const [failed, setFailed] = useState(false);
  const imageUrl = getCoverImageUrl(book);

  if (!imageUrl || failed) {
    return (
      <div className="w-36 h-48 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
        <BookOpen size={36} className="text-gray-400" />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={book.title || 'Book cover'}
      className="w-36 h-48 rounded-lg object-cover flex-shrink-0 shadow-sm"
      onError={() => setFailed(true)}
    />
  );
}

interface FeedbackModalProps {
  book: CoverModerationBook;
  onClose: () => void;
  onSuccess: (bookId: string) => void;
}

function FeedbackModal({ book, onClose, onSuccess }: Readonly<FeedbackModalProps>) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitModerationFeedback({ book_id: book.book_id, feedback: text.trim() });
      onSuccess(book.book_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  }, [text, book.book_id, onSuccess]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Submit Feedback</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Book info */}
        <div className="px-6 pt-4 pb-2">
          <p className="text-sm font-medium text-gray-900 truncate">{book.title || '—'}</p>
          <p className="text-sm text-gray-500">{book.author || '—'}</p>
          <p className="text-xs text-gray-400 mt-0.5 font-mono">ISBN: {book.book_id}</p>
        </div>

        {/* Textarea */}
        <div className="px-6 py-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter your feedback…"
            rows={4}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            disabled={submitting}
            // biome-ignore lint/a11y/noAutofocus: internal moderation tool — focus jumps into the feedback box on open by design
            autoFocus
          />
          {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !text.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

const PAGE_SIZE = 50;

type StatusFilter = 'safe' | 'flagged' | null;

export default function ModerationPage() {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(null);
  const [feedbackBook, setFeedbackBook] = useState<CoverModerationBook | null>(null);
  const [submittedIds, setSubmittedIds] = useState<Set<string>>(new Set());

  const toggleFilter = useCallback((filter: StatusFilter) => {
    setStatusFilter((prev) => (prev === filter ? null : filter));
    setPage(0);
  }, []);

  // Explicit generics: newer @tanstack/react-query types lose inference through
  // the identity-function placeholderData on TypeScript 4.9, collapsing `data` to any.
  const { data, isLoading, isError, error } = useQuery<CoverModerationResponse, Error>({
    queryKey: ['cover-moderation', page, statusFilter],
    queryFn: () => getCoverModerationData(PAGE_SIZE, page * PAGE_SIZE, statusFilter ?? undefined),
    placeholderData: (prev) => prev,
  });

  const handleFeedbackSuccess = useCallback((bookId: string) => {
    setSubmittedIds((prev) => new Set(prev).add(bookId));
    setFeedbackBook(null);
  }, []);

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  const flaggedCount =
    data?.stats?.filter((s) => s.explicit).reduce((sum, s) => sum + s.count, 0) ?? 0;

  const safeCount =
    data?.stats?.filter((s) => !s.explicit).reduce((sum, s) => sum + s.count, 0) ?? 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center gap-4">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Shield className="text-indigo-600" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Cover Moderation</h1>
            <p className="text-sm text-gray-500">
              Visual verification of book cover moderation results
            </p>
          </div>
          {data && (
            <span className="ml-auto text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {data.total.toLocaleString()} scanned
            </span>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Stats Cards */}
        {(data?.stats?.length ?? 0) > 0 && (
          <div className="grid grid-cols-3 gap-4">
            {/* Total scanned */}
            <button
              type="button"
              onClick={() => setStatusFilter(null)}
              className={`text-left rounded-xl border p-5 shadow-sm transition-all ${
                statusFilter === null
                  ? 'border-indigo-400 ring-2 ring-indigo-200 bg-indigo-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-1.5 bg-indigo-100 rounded-lg">
                  <Shield size={18} className="text-indigo-600" />
                </div>
                <span className="text-sm font-medium text-gray-500">Total Scanned</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{data?.total.toLocaleString()}</p>
            </button>

            {/* Flagged */}
            <button
              type="button"
              onClick={() => toggleFilter('flagged')}
              className={`text-left rounded-xl border p-5 shadow-sm transition-all ${
                statusFilter === 'flagged'
                  ? 'border-red-400 ring-2 ring-red-200 bg-red-50'
                  : 'border-red-200 bg-white hover:border-red-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-1.5 bg-red-100 rounded-lg">
                  <ShieldAlert size={18} className="text-red-600" />
                </div>
                <span className="text-sm font-medium text-gray-500">Flagged</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{flaggedCount.toLocaleString()}</p>
            </button>

            {/* Safe */}
            <button
              type="button"
              onClick={() => toggleFilter('safe')}
              className={`text-left rounded-xl border p-5 shadow-sm transition-all ${
                statusFilter === 'safe'
                  ? 'border-emerald-400 ring-2 ring-emerald-200 bg-emerald-50'
                  : 'border-emerald-200 bg-white hover:border-emerald-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-1.5 bg-emerald-100 rounded-lg">
                  <ShieldCheck size={18} className="text-emerald-600" />
                </div>
                <span className="text-sm font-medium text-gray-500">Safe</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{safeCount.toLocaleString()}</p>
            </button>
          </div>
        )}

        {/* Loading */}
        {isLoading && !data && (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="text-indigo-500 animate-spin" />
            <span className="ml-3 text-gray-500">Loading moderation data...</span>
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertTriangle className="mx-auto text-red-500 mb-2" size={28} />
            <p className="text-red-700 font-medium">Failed to load moderation data</p>
            <p className="text-red-500 text-sm mt-1">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        )}

        {/* Table */}
        {(data?.books?.length ?? 0) > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                      Book
                    </th>
                    <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                      Status
                    </th>
                    <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                      Reason
                    </th>
                    <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                      Scanned
                    </th>
                    <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                      Version
                    </th>
                    <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.books.map((book: CoverModerationBook, idx: number) => {
                    const badge = getStatusBadge(book);
                    return (
                      <tr key={book.book_id || idx} className="hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-4">
                            <CoverThumbnail book={book} />
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate max-w-xs">
                                {book.title || '—'}
                              </p>
                              <p className="text-sm text-gray-500 truncate max-w-xs">
                                {book.author || '—'}
                              </p>
                              <p className="text-xs text-gray-400 font-mono mt-0.5">
                                {book.book_id}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${badge.bg} ${badge.text} ${badge.border}`}
                          >
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-gray-600 max-w-md">
                          {book.cover_explicit_reason || '—'}
                        </td>
                        <td className="px-5 py-4 text-gray-500 whitespace-nowrap">
                          {formatDate(book.cover_moderation_date)}
                        </td>
                        <td className="px-5 py-4 text-gray-500">
                          {book.cover_moderation_version || '—'}
                        </td>
                        <td className="px-5 py-4">
                          {submittedIds.has(book.book_id) ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                              <CheckCircle2 size={14} />
                              Submitted
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setFeedbackBook(book)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                            >
                              <MessageSquarePlus size={14} />
                              Feedback
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-500">
                  Showing {(page * PAGE_SIZE + 1).toLocaleString()}–
                  {Math.min((page + 1) * PAGE_SIZE, data?.total ?? 0).toLocaleString()} of{' '}
                  {data?.total?.toLocaleString()}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <span className="px-3 py-1.5 text-sm font-medium text-gray-700">
                    {page + 1} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page + 1 >= totalPages}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {data?.books?.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Shield className="mx-auto text-gray-300 mb-3" size={40} />
            <p className="text-gray-500 font-medium">
              {statusFilter
                ? 'No books match the selected filter'
                : 'No cover moderation data found'}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {statusFilter ? (
                <button
                  type="button"
                  onClick={() => setStatusFilter(null)}
                  className="text-indigo-500 hover:underline"
                >
                  Clear filter
                </button>
              ) : (
                'Books with COVER_EXPLICIT will appear here once scanned'
              )}
            </p>
          </div>
        )}
      </main>

      {feedbackBook && (
        <FeedbackModal
          book={feedbackBook}
          onClose={() => setFeedbackBook(null)}
          onSuccess={handleFeedbackSuccess}
        />
      )}
    </div>
  );
}
